import SpeakerAiVoicesInfoTab from '@src/components/aiVoices/SpeakerAiVoicesInfoTab';
import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import { TabBar, TabButton } from '@src/components/aiVoices/voiceMapperStyles';
import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Input from '@src/components/common/Input';
import ItemDropdownSelect from '@src/components/common/ItemDropdownSelect';
import Textarea from '@src/components/common/Textarea';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import { useOverlayDismiss } from '@src/hooks/useOverlayDismiss';
import { useSpeakerFormHeadshots } from '@src/hooks/useSpeakerFormHeadshots';
import ChatGPTService from '@src/services/ChatGPTService';
import { useDatoClient, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CloseIcon from '@src/svgs/CloseIcon';
import EditSparkleIcon from '@src/svgs/EditSparkleIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import SpeakerHeadshotGrid from './SpeakerHeadshotGrid';

import type { SpeakerData } from '@src/services/SpeakerManagementService';

const logger = LogManager.getLogger('component:SpeakerFormModal');

type GenderValue = NonNullable<SpeakerData['gender']>;

/**
 * Gender options (PLAT-11692). "Not set" is a first-class choice, not a
 * placeholder: it must stay selectable so an editor can clear a value they set
 * by mistake, and it is what every speaker predating the field reads as.
 */
const GENDER_OPTIONS: Array<{ value: GenderValue; caption: string }> = [
  { value: '', caption: 'Not set' },
  { value: 'male', caption: 'Male' },
  { value: 'female', caption: 'Female' },
  { value: 'neutral', caption: 'Neutral' },
];

interface SpeakerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Callback for submit handling (create/update the speaker).
  onSubmit: (speakerData: SpeakerData) => Promise<boolean>;
  // Optional "fire-and-forget" create handler (Creator Studio "Add Speaker").
  // When provided and NOT in edit mode, the modal closes immediately and hands
  // the headshot upload + save off to the parent, which renders a skeleton
  // "saving" row while the work completes in the background.
  onSubmitBackground?: (args: {
    data: Omit<SpeakerData, 'headshots'>;
    uploadPromise: Promise<
      Array<{ id: string; url: string }> | null | undefined
    >;
    previewUrls: string[];
  }) => void;
  // For editing mode
  speakerToEdit?: {
    speakerLabel: string;
    datoSpeakerId: string;
    name: string;
    title?: string;
    bio?: string;
    voice?: string;
    gender?: SpeakerData['gender'] | null;
    headshot?: string | null;
    headshots?: Array<{ id: string; url: string }>;
  };
  /**
   * Raise this overlay above the dialog that opened it. Pass
   * `NESTED_IN_MODAL_Z` when opening from inside a shared `Modal`, whose
   * backdrop otherwise resolves higher than this one's and paints over it.
   * Omit from an ordinary panel — the resting z is correct there.
   */
  zIndex?: string;
}

/**
 * Unified modal for creating new speakers or editing existing ones
 */
const SpeakerFormModal: React.FC<SpeakerFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSubmitBackground,
  speakerToEdit,
  zIndex,
}) => {
  const isEditMode = !!speakerToEdit;
  const userIdentity = useUserIdentity();
  const { enableAiVoices } = useFlagsCombination(userIdentity.currentRole);
  const { dubbingEnabled } = useVoiceActivation();
  const [activeFormTab, setActiveFormTab] = useState<'info' | 'aiVoices'>(
    'info',
  );
  // Initialize state with either existing speaker data or empty values
  const [name, setName] = useState(speakerToEdit?.name || '');
  const [title, setTitle] = useState(speakerToEdit?.title || '');
  const [bio, setBio] = useState(speakerToEdit?.bio || '');
  const [voice, setVoice] = useState(speakerToEdit?.voice || '');
  const [gender, setGender] = useState<GenderValue>(
    speakerToEdit?.gender || '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);
  const [showRegenerateInput, setShowRegenerateInput] = useState(false);
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
  const [showRegenerateVoiceInput, setShowRegenerateVoiceInput] =
    useState(false);
  const [regenerateVoiceInstructions, setRegenerateVoiceInstructions] =
    useState('');

  // Headshot state managed by dedicated hook
  const {
    allHeadshotPreviews,
    existingHeadshotsCount,
    isUploadingHeadshot,
    headshotInputRef,
    handleHeadshotSelect,
    handleRemoveExisting,
    handleRemoveNew,
    triggerFileInput,
    uploadHeadshotsIfNeeded,
    reset: resetHeadshots,
  } = useSpeakerFormHeadshots(speakerToEdit);

  // Sync form values when modal opens or speaker changes
  // Use speakerToEdit properties directly to avoid object reference issues
  const speakerId = speakerToEdit?.datoSpeakerId;
  const speakerName = speakerToEdit?.name;
  // The dubbing-gated "AI Voices" tab is offered only for a saved speaker
  // (needs a Dato id) when the feature + workspace dubbing are on (DE4).
  const showAiVoicesTab = Boolean(
    enableAiVoices && dubbingEnabled && speakerId,
  );
  const modalTitle = isEditMode
    ? speakerToEdit?.datoSpeakerId
      ? 'Edit Speaker'
      : 'Create & Assign Speaker'
    : 'Create New Speaker';
  const speakerTitle = speakerToEdit?.title;
  const speakerBio = speakerToEdit?.bio;
  const speakerVoice = speakerToEdit?.voice;
  const speakerGender = speakerToEdit?.gender;

  // Load the form from the speaker when the modal OPENS or a different speaker
  // is being edited — and at no other time.
  //
  // This used to depend on every individual field too, which made any re-render
  // of the HOST reset the form mid-edit: `CreatorStudioSpeakersPanel` is a MobX
  // observer that re-renders throughout playback and builds `speakerToEdit`
  // inline with `headshots: speaker.headshots || []`, so a speaker with no
  // headshot produced a fresh array identity on every one of those renders. A
  // just-picked gender (or a half-typed name, or freshly selected headshot
  // files — `resetHeadshots` runs here too) was discarded within a frame.
  // Not depending on the values is the point: once the form is open it owns
  // them, and an upstream change must not clobber what the user is typing.
  useEffect(() => {
    if (isOpen) {
      setName(speakerName || '');
      setTitle(speakerTitle || '');
      setBio(speakerBio || '');
      setVoice(speakerVoice || '');
      setGender(speakerGender || '');
      setError(null);
      setShowRegenerateInput(false);
      setRegenerateInstructions('');
      setShowRegenerateVoiceInput(false);
      setRegenerateVoiceInstructions('');
      resetHeadshots();
    }
  }, [isOpen, speakerId]);

  // Get videoCreator and create ChatGPTService instance
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const chatGPTServiceRef = useRef<ChatGPTService | null>(null);

  // Initialize ChatGPTService when component mounts
  useEffect(() => {
    if (videoCreator && datoClientStore?.aiPromptRepository) {
      chatGPTServiceRef.current = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
    }
  }, [videoCreator, datoClientStore]);

  // Add refs for modal container and first input
  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { handleOverlayMouseDown, handleOverlayClick } =
    useOverlayDismiss(onClose);

  // Handle escape key, auto-focus, and event propagation
  useEffect(() => {
    if (!isOpen) return;

    // Handle escape key to close modal
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Auto-focus the name input when modal opens
    if (nameInputRef.current) {
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 50);
    }

    // Prevent selection changes when interacting with the modal
    const handleModalEvents = (e: Event) => {
      e.stopPropagation(); // Prevent event from bubbling up
    };

    // Get the modal and overlay elements
    const modalElement = modalRef.current;
    const overlayElement = modalElement?.parentElement; // The ModalOverlay is parent of ModalContent

    // Add event listeners to the modal content
    if (modalElement) {
      modalElement.addEventListener('mousedown', handleModalEvents, true);
      modalElement.addEventListener('mouseup', handleModalEvents, true);
      modalElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add event listeners to the modal overlay
    if (overlayElement) {
      overlayElement.addEventListener('mousedown', handleModalEvents, true);
      overlayElement.addEventListener('mouseup', handleModalEvents, true);
      overlayElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // On `window` so an open dropdown (useAnchoredDropdown stops Escape at
    // `document`) closes just itself, not the modal.
    window.addEventListener('keydown', handleEscapeKey);

    return () => {
      // Clean up all event listeners
      window.removeEventListener('keydown', handleEscapeKey);

      // Clean up modal content listeners
      if (modalElement) {
        modalElement.removeEventListener('mousedown', handleModalEvents, true);
        modalElement.removeEventListener('mouseup', handleModalEvents, true);
        modalElement.removeEventListener('mousemove', handleModalEvents, true);
      }

      // Clean up overlay listeners
      if (overlayElement) {
        overlayElement.removeEventListener(
          'mousedown',
          handleModalEvents,
          true,
        );
        overlayElement.removeEventListener('mouseup', handleModalEvents, true);
        overlayElement.removeEventListener(
          'mousemove',
          handleModalEvents,
          true,
        );
      }
    };
  }, [isOpen, onClose]);

  // Get the portal container (document.body for full-screen overlay)
  const portalContainer =
    typeof document !== 'undefined' ? document.body : null;

  if (!isOpen || !portalContainer) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Speaker name is required');
      return;
    }

    // Creator Studio "Add Speaker": kick off the upload + save and let the
    // parent render a skeleton "saving" row. Close immediately rather than
    // blocking the modal on the network round-trip.
    if (onSubmitBackground && !isEditMode) {
      // Start the upload now so it captures the current files; the parent
      // awaits this promise (it never rejects — failures resolve to undefined).
      const uploadPromise = uploadHeadshotsIfNeeded();
      onSubmitBackground({
        data: {
          name: name.trim(),
          title: title.trim() || undefined,
          bio: bio.trim() || undefined,
          voice: voice.trim() || undefined,
          gender,
        },
        uploadPromise,
        previewUrls: allHeadshotPreviews,
      });
      setName('');
      setTitle('');
      setBio('');
      setVoice('');
      setGender('');
      onClose();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Upload headshots if new files were selected or existing ones removed
      const headshotsResult = await uploadHeadshotsIfNeeded();

      const dataToSubmit = {
        name: name.trim(),
        title: title.trim() || undefined,
        bio: bio.trim() || undefined,
        voice: voice.trim() || undefined,
        gender,
        headshots: headshotsResult !== undefined ? headshotsResult : undefined,
      };
      const success = await onSubmit(dataToSubmit);
      if (!success) {
        logger.error('onSubmit callback returned false', {
          dataToSubmit,
        });
        throw new Error('Failed to create speaker');
      }

      // Reset form and close modal on success
      setName('');
      setTitle('');
      setBio('');
      setVoice('');
      setGender('');
      onClose();
    } catch (err) {
      logger.error(
        `Error ${isEditMode ? 'updating' : 'creating'} speaker`,
        extractErrorInfo(err),
      );
      setError(
        `Failed to ${
          isEditMode ? 'update' : 'create'
        } speaker. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateBio = async (customInstructions?: string) => {
    if (!name || !chatGPTServiceRef.current) return;

    setIsGeneratingBio(true);
    setError(null);

    try {
      const generatedBio = await chatGPTServiceRef.current.generateSpeakerBio({
        speakerName: name,
        speakerRole: title || undefined,
        selectedSpeakers: [],
        customInstructions,
        existingBio: customInstructions ? bio : undefined, // Pass existing bio when regenerating
      });

      if (generatedBio) {
        setBio(generatedBio);
        // Reset regeneration UI after successful regeneration
        if (customInstructions) {
          setShowRegenerateInput(false);
          setRegenerateInstructions('');
        }
      }
    } catch (err) {
      logger.error('Error generating bio', extractErrorInfo(err));
      setError('Failed to generate bio. Please try again.');
    } finally {
      setIsGeneratingBio(false);
    }
  };

  const handleGenerateVoice = async (customInstructions?: string) => {
    if (!name || !chatGPTServiceRef.current) return;

    setIsGeneratingVoice(true);
    setError(null);

    try {
      const generatedVoice =
        await chatGPTServiceRef.current.generateSpeakerVoice({
          speakerName: name,
          speakerRole: title || undefined,
          selectedSpeakers: [],
          customInstructions,
          existingVoice: customInstructions ? voice : undefined, // Pass existing voice when regenerating
        });

      if (generatedVoice) {
        setVoice(generatedVoice);
        // Reset regeneration UI after successful regeneration
        if (customInstructions) {
          setShowRegenerateVoiceInput(false);
          setRegenerateVoiceInstructions('');
        }
      }
    } catch (err) {
      logger.error('Error generating voice', extractErrorInfo(err));
      setError('Failed to generate voice description. Please try again.');
    } finally {
      setIsGeneratingVoice(false);
    }
  };

  // Use portal to render modal at document body level for proper overlay
  return createPortal(
    <ModalOverlay
      $zIndex={zIndex}
      onClick={handleOverlayClick}
      onMouseDown={handleOverlayMouseDown}
      data-selection-menu="true"
      style={{ userSelect: 'none' }}
      contentEditable={false}
    >
      <ModalContent
        $size="md"
        ref={modalRef}
        data-selection-menu="true"
        style={{ userSelect: 'none' }}
        contentEditable={false}
      >
        {/* The tab bar (dubbing on) visually doubles as the heading, but we
            always render a heading so the modal is never headingless for screen
            readers — visually hidden when the tab bar is shown. */}
        {showAiVoicesTab ? (
          <VisuallyHiddenHeading>{modalTitle}</VisuallyHiddenHeading>
        ) : (
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
          </DialogHeader>
        )}
        <CloseButton
          aria-label="Close"
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon />
        </CloseButton>

        <ModalBody>
          {showAiVoicesTab && (
            <FormTabBar role="tablist">
              <TabButton
                type="button"
                role="tab"
                aria-selected={activeFormTab === 'info'}
                $active={activeFormTab === 'info'}
                onClick={() => setActiveFormTab('info')}
              >
                Info
              </TabButton>
              <TabButton
                type="button"
                role="tab"
                aria-selected={activeFormTab === 'aiVoices'}
                $active={activeFormTab === 'aiVoices'}
                onClick={() => setActiveFormTab('aiVoices')}
              >
                AI Voices
              </TabButton>
            </FormTabBar>
          )}
          <TabPanel>
            {showAiVoicesTab && activeFormTab === 'aiVoices' ? (
              <SpeakerAiVoicesInfoTab
                speakerId={speakerId as string}
                speakerName={name || speakerName || 'Speaker'}
                speakerHeadshotUrl={
                  speakerToEdit?.headshot ?? speakerToEdit?.headshots?.[0]?.url
                }
              />
            ) : (
              <Form
                onSubmit={e => {
                  e.stopPropagation(); // Don't let the submit event propagate
                  handleSubmit(e);
                }}
              >
                <input
                  ref={headshotInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleHeadshotSelect}
                />
                <NameHeadshotRow>
                  <NameFormGroup>
                    <FieldLabel htmlFor="speakerName">Name</FieldLabel>
                    <Input
                      fullWidth
                      id="speakerName"
                      name="speaker-display-name-field"
                      ref={nameInputRef}
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Enter speaker name"
                      autoFocus
                      required
                      autoComplete="nope"
                      data-lpignore="true"
                      data-1p-ignore
                      data-form-type="other"
                    />
                  </NameFormGroup>
                  <SpeakerHeadshotGrid
                    allHeadshotPreviews={allHeadshotPreviews}
                    existingHeadshotsCount={existingHeadshotsCount}
                    isUploadingHeadshot={isUploadingHeadshot}
                    onRemoveExisting={handleRemoveExisting}
                    onRemoveNew={handleRemoveNew}
                    onAddClick={triggerFileInput}
                  />
                </NameHeadshotRow>

                <FormGroup>
                  <FieldLabel htmlFor="speakerTitle">Title/Role</FieldLabel>
                  <Input
                    fullWidth
                    id="speakerTitle"
                    name="speaker-role-title-field"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. CEO, Product Manager, etc."
                    autoComplete="nope"
                    data-lpignore="true"
                    data-1p-ignore
                    data-form-type="other"
                  />
                </FormGroup>

                <FormGroup>
                  {/* `as="span"`: the trigger is a div, so there is no form
                      control for `htmlFor` to target. */}
                  <FieldLabel as="span">Gender</FieldLabel>
                  <GenderField aria-label="Gender">
                    <ItemDropdownSelect
                      propertyName="gender"
                      defaultValue={gender}
                      values={GENDER_OPTIONS}
                      action={(_, value) =>
                        setGender(
                          GENDER_OPTIONS.find(o => o.value === value)?.value ??
                            '',
                        )
                      }
                    />
                  </GenderField>
                  <FieldHint>
                    Used for grammatical agreement in translations.
                  </FieldHint>
                </FormGroup>

                <FormGroup>
                  <LabelRow>
                    <FieldLabel htmlFor="speakerBio">Biography</FieldLabel>
                    <ButtonContainer>
                      {!bio &&
                        (isGeneratingBio ? (
                          <SparkleLoading />
                        ) : (
                          <GenerateButton
                            type="button"
                            onClick={() => handleGenerateBio()}
                            disabled={isGeneratingBio}
                          >
                            <EditSparkleIcon
                              width="16"
                              height="16"
                              strokeColor="currentColor"
                            />
                            Generate
                          </GenerateButton>
                        ))}
                      {bio && !showRegenerateInput && (
                        <GenerateButton
                          type="button"
                          onClick={() => setShowRegenerateInput(true)}
                          disabled={isGeneratingBio}
                        >
                          <EditSparkleIcon
                            width="16"
                            height="16"
                            strokeColor="currentColor"
                          />
                          Regenerate
                        </GenerateButton>
                      )}
                    </ButtonContainer>
                  </LabelRow>
                  <Textarea
                    fullWidth
                    id="speakerBio"
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    placeholder="Brief biography of the speaker"
                    disabled={isGeneratingBio}
                  />
                  {showRegenerateInput && (
                    <RegenerateContainer>
                      <Textarea
                        autoGrow
                        fullWidth
                        aria-label="Biography instructions"
                        value={regenerateInstructions}
                        onChange={e =>
                          setRegenerateInstructions(e.target.value)
                        }
                        placeholder="How would you like to improve the bio? (e.g., 'Make it shorter', 'Focus more on recent work', 'Add more personal details')"
                        disabled={isGeneratingBio}
                      />
                      <RegenerateActions>
                        <GenerateButton
                          type="button"
                          onClick={() => {
                            setShowRegenerateInput(false);
                            setRegenerateInstructions('');
                          }}
                          disabled={isGeneratingBio}
                        >
                          Cancel
                        </GenerateButton>
                        <GenerateButton
                          type="button"
                          onClick={() =>
                            handleGenerateBio(regenerateInstructions)
                          }
                          disabled={
                            isGeneratingBio || !regenerateInstructions.trim()
                          }
                        >
                          {isGeneratingBio ? 'Regenerating…' : 'Regenerate'}
                        </GenerateButton>
                      </RegenerateActions>
                    </RegenerateContainer>
                  )}
                </FormGroup>

                <FormGroup>
                  <LabelRow>
                    <FieldLabel htmlFor="speakerVoice">
                      Voice Description
                    </FieldLabel>
                    <ButtonContainer>
                      {!voice &&
                        (isGeneratingVoice ? (
                          <SparkleLoading />
                        ) : (
                          <GenerateButton
                            type="button"
                            onClick={() => handleGenerateVoice()}
                            disabled={isGeneratingVoice}
                          >
                            <EditSparkleIcon
                              width="16"
                              height="16"
                              strokeColor="currentColor"
                            />
                            Generate
                          </GenerateButton>
                        ))}
                      {voice && !showRegenerateVoiceInput && (
                        <GenerateButton
                          type="button"
                          onClick={() => setShowRegenerateVoiceInput(true)}
                          disabled={isGeneratingVoice}
                        >
                          <EditSparkleIcon
                            width="16"
                            height="16"
                            strokeColor="currentColor"
                          />
                          Regenerate
                        </GenerateButton>
                      )}
                    </ButtonContainer>
                  </LabelRow>
                  <Textarea
                    fullWidth
                    id="speakerVoice"
                    value={voice}
                    onChange={e => setVoice(e.target.value)}
                    placeholder="Describe speaker's voice – tone, style, etc."
                    disabled={isGeneratingVoice}
                  />
                  {showRegenerateVoiceInput && (
                    <RegenerateContainer>
                      <Textarea
                        autoGrow
                        fullWidth
                        aria-label="Voice description instructions"
                        value={regenerateVoiceInstructions}
                        onChange={e =>
                          setRegenerateVoiceInstructions(e.target.value)
                        }
                        placeholder="How would you like to improve the voice description? (e.g., 'More casual tone', 'Include speaking pace', 'Add humor style')"
                        disabled={isGeneratingVoice}
                      />
                      <RegenerateActions>
                        <GenerateButton
                          type="button"
                          onClick={() => {
                            setShowRegenerateVoiceInput(false);
                            setRegenerateVoiceInstructions('');
                          }}
                          disabled={isGeneratingVoice}
                        >
                          Cancel
                        </GenerateButton>
                        <GenerateButton
                          type="button"
                          onClick={() =>
                            handleGenerateVoice(regenerateVoiceInstructions)
                          }
                          disabled={
                            isGeneratingVoice ||
                            !regenerateVoiceInstructions.trim()
                          }
                        >
                          {isGeneratingVoice ? 'Regenerating…' : 'Regenerate'}
                        </GenerateButton>
                      </RegenerateActions>
                    </RegenerateContainer>
                  )}
                </FormGroup>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <Footer>
                  <SecondaryButton
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      onClose();
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    type="submit"
                    disabled={isSubmitting}
                    onClick={e => {
                      e.stopPropagation();
                      // Don't preventDefault as we want form submission to occur
                    }}
                  >
                    {isSubmitting ? 'Saving…' : 'Save'}
                  </PrimaryButton>
                </Footer>
              </Form>
            )}
          </TabPanel>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>,
    portalContainer,
  );
};

const SparkleLoading = () => (
  <SimpleLoadingSpinner
    spinnerWidth={3.5}
    spinnerSize={24}
    icon={<SparkleIcon />}
    iconSize={10}
    text={null}
  />
);

// Styled components
const ModalOverlay = styled.div<{ $zIndex?: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--modal-backdrop);
  backdrop-filter: var(--modal-blur);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${p => p.$zIndex ?? `var(${TOP_LAYER_VARS.modalBackdrop}, 1000006)`};
`;

// The shared dialog card (docs/design-system.md → Modals & Dialogs), plus the
// password-manager suppression this form has always needed.
const ModalContent = styled(DialogCard)`
  position: relative;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  /* Hide LastPass and other password manager injected elements */
  div[id^='__lpform_'],
  [data-lastpass-icon-root],
  [data-lastpass-root] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

// Standard close button (docs/design-system.md → Close Buttons), placed where
// the shared Modal puts its own.
const CloseButton = styled.button`
  position: absolute;
  right: var(--spacing-sm);
  top: var(--spacing-sm);
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: var(--spacing-sm);
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const ModalBody = styled.div`
  width: 100%;
`;

const TabPanel = styled.div``;

// Keeps an accessible heading for screen readers when the tab bar visually
// stands in for the title (AI Voices mode) — off-screen, not display:none.
const VisuallyHiddenHeading = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// The shared underline tab bar, with the modal's own spacing beneath it.
const FormTabBar = styled(TabBar)`
  margin-bottom: var(--spacing-lg);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: 100%;
  position: relative;

  /* Hide LastPass injected icon elements */
  div[id^='__lpform_'] {
    display: none !important;
  }
`;

// The same label the shared Input/Textarea primitives draw, kept here so it
// can carry an htmlFor and share a row with the Generate button.
const FieldLabel = styled.label`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

// The shared anchored dropdown (portaled, themed menu — design-system
// "Dropdown / Popover Menus"), restyled to the shared `Input` primitive's
// field tokens so it matches the Name / Title inputs above it.
//
// `color` is not cosmetic padding here: ItemDropdownSelect's trigger sets no
// colour and inherits one, and this modal establishes none. Without it the
// selected caption renders in the ambient near-black and reads as empty.
const GenderField = styled.div`
  .Selected {
    box-sizing: border-box;
    height: calc(var(--leading-text-sm) + 2 * var(--spacing-sm) + 2px);
    padding: var(--spacing-sm) var(--spacing-md);
    background-color: var(--input-background);
    border-color: var(--border);
    border-radius: var(--radius-input);
    color: var(--foreground);
    font-size: var(--text-sm);
    line-height: var(--leading-text-sm);
  }
`;

const FieldHint = styled.span`
  display: block;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

// Label + inline action. Pinned to the small button height so the row does
// not jump when the Generate button gives way to the spinner or disappears.
const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: var(--btn-h-sm);
`;

const smButton = css`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  font-size: var(--text-xs);
`;

// Generate / Regenerate are contextual utilities, not the commit action — quiet
// secondary styling keeps Save the single green button in the dialog.
const GenerateButton = styled(SecondaryButton)`
  ${smButton}
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: var(--spacing-sm);
`;

const RegenerateContainer = styled.div`
  margin-top: var(--spacing-xs);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const RegenerateActions = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
`;

const ErrorMessage = styled.div`
  color: var(--destructive);
  font-size: var(--text-sm);
`;

const Footer = styled(DialogFooter)`
  margin-top: var(--spacing-sm);
`;

const NameHeadshotRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
`;

// Fills whatever the fixed-width headshot column leaves, so its width never
// changes as headshots come and go.
const NameFormGroup = styled(FormGroup)`
  flex: 1 1 0;
  min-width: 0;
`;

export default SpeakerFormModal;
